/**
 * FrontEnd/my-app/src/crm/portal/PortalDashboard.js
 * Counterparty Portal Main Repository Dashboard.
 * Lists company-scoped documents, action filters, and opens the review/signing drawer.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import BASE_URL from '../../url';
import './PortalStyles.css';
import PortalDocumentViewer from './PortalDocumentViewer';
import PortalCompanyOverview from './PortalCompanyOverview';
import {
  LuFileText, LuShieldCheck, LuLogOut, LuSearch,
  LuClock, LuCircleCheck, LuArrowRight, LuEye, LuCircleAlert,
  LuBuilding2
} from 'react-icons/lu';

const PortalDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: pathDocId } = useParams();

  const [sessionUser, setSessionUser] = useState(() => {
    try {
      const saved = localStorage.getItem('portal_user');
      return saved ? JSON.parse(saved) : null;
    } catch (_) {
      return null;
    }
  });
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('OVERVIEW'); // 'OVERVIEW' | 'ALL' | 'ACTION_REQUIRED' | 'EXECUTED'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDocId, setSelectedDocId] = useState(null);

  // Check URL param or route param for deep linked document
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const docParam = pathDocId || params.get('doc') || params.get('dest')?.replace('/documents/', '');
    if (docParam) {
      setSelectedDocId(docParam);
      setActiveTab('ALL');
    }
    const tabParam = params.get('tab');
    if (tabParam) {
      setActiveTab(tabParam.toUpperCase());
    }
  }, [pathDocId, location.search]);

  // Auth headers
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('portal_token');
    return {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true
    };
  }, []);

  // Fetch session
  const verifySession = useCallback(async () => {
    const token = localStorage.getItem('portal_token');
    if (!token) {
      navigate('/portal/login');
      return;
    }

    try {
      const res = await axios.get(`${BASE_URL}/api/portal/session`, getAuthHeaders());
      if (res.data.status === 'ok') {
        setSessionUser(res.data);
      }
    } catch (err) {
      localStorage.removeItem('portal_token');
      localStorage.removeItem('portal_user');
      navigate('/portal/login');
    }
  }, [navigate, getAuthHeaders]);

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/portal/documents`, {
        ...getAuthHeaders(),
        params: {
          q: searchTerm.trim() || undefined,
          status: activeTab === 'EXECUTED' ? 'EXECUTED' : undefined
        }
      });
      setDocuments(res.data.items || []);
    } catch (err) {
      console.error('Error fetching portal documents:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, searchTerm, activeTab]);

  useEffect(() => {
    verifySession();
  }, [verifySession]);

  useEffect(() => {
    if (sessionUser) {
      fetchDocuments();
    }
  }, [sessionUser, fetchDocuments]);

  // Logout handler
  const handleLogout = async () => {
    try {
      await axios.post(`${BASE_URL}/api/portal/auth/logout`, {}, getAuthHeaders());
    } catch (_) {}
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_user');
    navigate('/portal/login');
  };

  // Filtered documents
  const filteredDocs = documents.filter(d => {
    if (activeTab === 'ACTION_REQUIRED') {
      return d.requiresMyAction;
    }
    if (activeTab === 'EXECUTED') {
      return d.statusCode === 'EXECUTED';
    }
    return true;
  });

  const pendingActionCount = documents.filter(d => d.requiresMyAction).length;
  const nextActionDoc = documents.find(d => d.requiresMyAction);

  return (
    <div className="cp-layout">
      {/* ─── Top Navbar ───────────────────────────────────────────────────── */}
      <header className="cp-navbar">
        <div className="cp-brand">
          <div className="cp-logo-icon">
            <LuShieldCheck size={20} />
          </div>
          <div>
            <span className="cp-brand-name">Prophecy Portal</span>
            <span className="cp-brand-tag" style={{ marginLeft: '8px' }}>Partner Gateway</span>
          </div>
        </div>

        <div className="cp-user-profile">
          <div className="cp-user-info">
            <div className="cp-user-name">
              {sessionUser?.contact?.firstName} {sessionUser?.contact?.lastName}
            </div>
            <div className="cp-user-company">
              {sessionUser?.company?.name}
            </div>
          </div>

          <button type="button" onClick={handleLogout} className="cp-btn-logout">
            <LuLogOut size={14} style={{ marginRight: '4px' }} /> Sign Out
          </button>
        </div>
      </header>

      {/* ─── Main Content ─────────────────────────────────────────────────── */}
      <main className="cp-main">
        {/* Hero Card for Pending Actions */}
        {activeTab !== 'OVERVIEW' && pendingActionCount > 0 && nextActionDoc && (
          <div className="cp-hero-card">
            <div className="cp-hero-content">
              <h2>Signature Required ({pendingActionCount})</h2>
              <p>
                You have {pendingActionCount} agreement(s) awaiting your electronic signature on behalf of <strong>{sessionUser?.company?.name}</strong>.
              </p>
            </div>
            <div className="cp-hero-action">
              <button
                type="button"
                className="cp-btn-primary"
                style={{ background: '#ffffff', color: 'var(--cp-auth)', width: 'auto' }}
                onClick={() => setSelectedDocId(nextActionDoc.commercialDocumentId)}
              >
                Review &amp; Sign {nextActionDoc.documentNumber} <LuArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Controls Bar: Tabs & Search */}
        <div className="cp-controls-bar">
          <div className="cp-tabs">
            <button
              type="button"
              className={`cp-tab-btn ${activeTab === 'OVERVIEW' ? 'active' : ''}`}
              onClick={() => setActiveTab('OVERVIEW')}
            >
              Overview
            </button>
            <button
              type="button"
              className={`cp-tab-btn ${activeTab === 'ALL' ? 'active' : ''}`}
              onClick={() => setActiveTab('ALL')}
            >
              All Documents <span className="cp-tab-count">{documents.length}</span>
            </button>
            <button
              type="button"
              className={`cp-tab-btn ${activeTab === 'ACTION_REQUIRED' ? 'active' : ''}`}
              onClick={() => setActiveTab('ACTION_REQUIRED')}
            >
              Action Required <span className="cp-tab-count" style={{ background: pendingActionCount > 0 ? '#ea580c' : undefined, color: pendingActionCount > 0 ? '#fff' : undefined }}>{pendingActionCount}</span>
            </button>
            {/* <button
              type="button"
              className={`cp-tab-btn ${activeTab === 'EXECUTED' ? 'active' : ''}`}
              onClick={() => setActiveTab('EXECUTED')}
            >
              Executed / Signed
            </button> */}
          </div>

          {activeTab !== 'OVERVIEW' && (
            <div className="cp-search-box">
              <LuSearch size={16} color="var(--cp-muted)" />
              <input
                type="text"
                placeholder="Search by title or document #..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          )}
        </div>

        {/* Tab Content: OVERVIEW vs Documents */}
        {activeTab === 'OVERVIEW' ? (
          <PortalCompanyOverview
            companyId={sessionUser?.company?.companyId}
            sessionUser={sessionUser}
            onCompanyUpdate={(updatedName) => {
              if (updatedName) {
                setSessionUser(prev => prev ? {
                  ...prev,
                  company: { ...prev.company, name: updatedName }
                } : prev);
              }
            }}
          />
        ) : (
          /* Document Table Card */
          <div className="cp-table-card">
          <table className="cp-table">
            <thead>
              <tr>
                <th>Document</th>
                <th>Type</th>
                <th>Status</th>
                <th>My Signature</th>
                <th>Obligations</th>
                <th>Last Updated</th>
                <th style={{ textAlign: 'right' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: 'var(--cp-muted)' }}>
                    Loading your documents...
                  </td>
                </tr>
              ) : filteredDocs.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: 'var(--cp-muted)' }}>
                    {searchTerm ? 'No documents match your search.' : 'No visible documents found for your company.'}
                  </td>
                </tr>
              ) : (
                filteredDocs.map(d => (
                  <tr key={d.commercialDocumentId}>
                    <td>
                      <div style={{ fontWeight: 600, color: 'var(--cp-ink)' }}>
                        {d.documentNumber}
                      </div>
                      <div style={{ fontSize: '12.5px', color: 'var(--cp-muted)', marginTop: '2px' }}>
                        {d.title}
                      </div>
                    </td>
                    <td>
                      <span style={{ fontWeight: 600, fontSize: '12px', color: '#475569', background: '#f1f5f9', padding: '2px 8px', borderRadius: '4px' }}>
                        {d.documentTypeCode}
                      </span>
                    </td>
                    <td>
                      <span className={`cp-badge ${d.statusCode === 'EXECUTED' ? 'cp-badge-executed' : d.statusCode === 'DECLINED' ? 'cp-badge-declined' : 'cp-badge-out'}`}>
                        {d.statusCode}
                      </span>
                    </td>
                    <td>
                      {d.requiresMyAction ? (
                        <span className="cp-badge cp-badge-action">
                          <LuCircleAlert size={12} /> ACTION REQUIRED
                        </span>
                      ) : d.mySignerStatus === 'SIGNED' ? (
                        <span className="cp-badge cp-badge-executed">
                          <LuCircleCheck size={12} /> SIGNED
                        </span>
                      ) : (
                        <span style={{ color: 'var(--cp-muted)', fontSize: '13px' }}>
                          {d.mySignerStatus || '—'}
                        </span>
                      )}
                    </td>
                    <td>
                      {d.openRequirementCount > 0 ? (
                        <span style={{ color: 'var(--cp-warn)', fontWeight: 600, fontSize: '12px', background: 'var(--cp-warn-light)', padding: '2px 6px', borderRadius: '4px' }}>
                          {d.openRequirementCount} open
                        </span>
                      ) : (
                        <span style={{ color: 'var(--cp-muted)', fontSize: '13px' }}>None</span>
                      )}
                    </td>
                    <td style={{ fontSize: '13px', color: 'var(--cp-muted)' }}>
                      {d.updatedAtUtc ? new Date(d.updatedAtUtc).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        type="button"
                        onClick={() => setSelectedDocId(d.commercialDocumentId)}
                        className={d.requiresMyAction ? "cp-btn-primary" : "cp-btn-logout"}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', padding: '6px 12px', width: 'auto' }}
                      >
                        {d.requiresMyAction ? (
                          <>Review &amp; Sign <LuArrowRight size={14} /></>
                        ) : (
                          <><LuEye size={14} /> View</>
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        )}
      </main>

      {/* ─── Document Detail & Signer Modal ───────────────────────────────── */}
      {selectedDocId && (
        <PortalDocumentViewer
          documentId={selectedDocId}
          onClose={() => {
            setSelectedDocId(null);
            if (pathDocId) {
              navigate('/portal', { replace: true });
            }
          }}
          onActionSuccess={fetchDocuments}
        />
      )}
    </div>
  );
};

export default PortalDashboard;
